import { Check, ArrowRight, Target } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const TIERS = [
  {
    id: "local-foundation",
    name: "Local Foundation",
    price: "$497",
    cadence: "one-time",
    delivery: "Delivered in 14 days",
    testid: "package-tier-1",
    features: [
      "Google Business Profile full optimization",
      "NAP audit and citation cleanup across priority platforms",
      "Website conversion basics: click-to-call, forms, mobile check",
      "Review profiles claimed and set up (Google + top industry platforms)",
    ],
    outcome: "Improve consistency across priority listings and strengthen local visibility.",
    badge: "One-time foundation",
    featured: false,
  },
  {
    id: "growth-engine",
    name: "Growth Engine",
    price: "$997",
    cadence: "per month",
    delivery: "First-month Foundation included",
    testid: "package-tier-2",
    features: [
      "Review generation and reputation management system",
      "30-day lead-generation funnel, built and launched",
      "Ongoing local SEO and content",
      "Monthly report with real KPIs: calls, direction requests, form fills, rankings",
    ],
    outcome: "Build and measure systems designed to attract customer inquiries.",
    badge: "Monthly growth",
    featured: true,
  },
  {
    id: "market-domination",
    name: "Market Domination",
    price: "$1,997",
    cadence: "per month",
    delivery: "Everything in Growth Engine, plus:",
    testid: "package-tier-3",
    features: [
      "Brand positioning and expanded directory presence by industry",
      "Content engine: short-form video and social campaigns",
      "Paid ad management (ad spend billed separately)",
      "Quarterly strategy session with executive dashboard",
    ],
    outcome: "Strengthen brand positioning and expand your local market presence.",
    badge: "Advanced monthly",
    featured: false,
  },
];

const ROWS = [
  { label: "Google Business Profile full optimization", v: [true, true, true] },
  { label: "NAP audit and citation cleanup (priority platforms)", v: [true, true, true] },
  { label: "Website conversion basics: click-to-call, forms, mobile check", v: [true, true, true] },
  { label: "Review profiles claimed and set up (Google + top industry platforms)", v: [true, true, true] },
  { label: "Review generation and reputation management system", v: [false, true, true] },
  { label: "30-day lead-generation funnel, built and launched", v: [false, true, true] },
  { label: "Ongoing local SEO and content", v: [false, true, true] },
  { label: "Monthly KPI report: calls, direction requests, form fills, rankings", v: [false, "Monthly", "Monthly"] },
  { label: "Brand positioning and expanded directory presence by industry", v: [false, false, true] },
  { label: "Content engine: short-form video and social campaigns", v: [false, false, true] },
  { label: "Paid ad management (ad spend billed separately)", v: [false, false, true] },
  { label: "Quarterly strategy session with executive dashboard", v: [false, false, "Quarterly"] },
];

export const ServicePackages = () => (
  <section id="packages" className="relative py-24 sm:py-32 bg-obsidian-900/50 border-y border-slate-800/60 grain">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <Eyebrow>Marketing Packages</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mt-4">
          Choose Your Growth Package
        </h2>
        <p className="text-slate-400 mt-4 max-w-3xl leading-relaxed">
          The $9.99 Snapshot and $99.99 Assessment are fixed-price diagnostics. Packages are scoped
          services — "starting at" pricing reflects your market, business size and scope. Every
          package begins with a quote after a short consultation. No blank-check quotes.
        </p>
      </Reveal>

      <div className="grid lg:grid-cols-3 gap-6 mt-14 items-stretch">
        {TIERS.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.1} className="h-full">
            <div
              className={`relative h-full rounded-3xl p-8 flex flex-col transition-transform duration-500 hover:-translate-y-1 ${
                t.featured
                  ? "bg-obsidian-800 border border-gold/40 gold-ring"
                  : "bg-obsidian-800/60 border border-slate-700/60 hover:border-gold/40"
              }`}
            >
              <span
                className={`absolute -top-3 left-8 rounded-full font-mono text-[10px] font-semibold tracking-[0.2em] uppercase px-4 py-1.5 ${
                  t.featured
                    ? "bg-gold text-obsidian"
                    : "bg-obsidian-700 text-slate-300 border border-slate-600"
                }`}
                data-testid={`${t.testid}-badge`}
              >
                {t.badge}
              </span>
              <h3 className="font-display text-2xl font-bold text-white">{t.name}</h3>
              <div className="flex items-end gap-2 mt-6">
                <span className="text-slate-500 text-sm mb-2">Starting at</span>
                <span className="font-display text-4xl font-extrabold text-white">{t.price}</span>
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mt-1">{t.cadence}</p>
              <p className="text-slate-400 text-xs mt-3">{t.delivery}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 px-4 py-3.5 flex gap-3">
                <Target className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-300 leading-relaxed">
                  <span className="text-emerald-400 font-semibold">Outcome:</span> {t.outcome}
                </p>
              </div>
              <Link
                to="/intake?type=consultation"
                data-testid={`${t.testid}-cta`}
                className="group mt-7 w-full text-center rounded-full py-3.5 font-display font-bold transition-all duration-300 inline-flex items-center justify-center gap-2"
                style={
                  t.featured
                    ? { background: "#D4AF37", color: "#062235" }
                    : { border: "1px solid rgba(212,175,55,0.6)", color: "#D4AF37" }
                }
              >
                Request a Quote
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.12} className="mt-14">
        <h3 className="font-display text-xl font-bold text-white text-center" data-testid="package-comparison-heading">
          Compare side by side
        </h3>
        <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-800" data-testid="package-comparison">
          <table className="w-full min-w-[720px] text-sm border-collapse">
            <thead>
              <tr className="bg-obsidian-800/80">
                <th className="px-5 py-4 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  What's included
                </th>
                {TIERS.map((t) => (
                  <th
                    key={t.id}
                    className={`px-5 py-4 text-center align-bottom ${t.featured ? "bg-gold/10" : ""}`}
                  >
                    <span className="block font-display font-bold text-white text-sm sm:text-base">{t.name}</span>
                    <span className="block text-gold text-xs mt-1">
                      Starting at {t.price}
                      {t.cadence === "per month" ? "/mo" : ""}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, ri) => (
                <tr
                  key={r.label}
                  data-testid={`package-comparison-row-${ri}`}
                  className={ri % 2 === 0 ? "bg-obsidian-900/40" : "bg-obsidian-800/30"}
                >
                  <td className="px-5 py-3.5 text-slate-300 leading-relaxed">{r.label}</td>
                  {r.v.map((val, ci) => (
                    <td key={ci} className={`px-5 py-3.5 text-center ${TIERS[ci].featured ? "bg-gold/10" : ""}`}>
                      {val === true ? (
                        <Check className="w-4 h-4 text-emerald-400 inline" aria-label="Included" />
                      ) : val === false ? (
                        <span className="text-slate-600">—</span>
                      ) : (
                        <span className="text-gold text-xs font-semibold">{val}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-obsidian-800/80 border-t border-slate-800">
                <td className="px-5 py-4" />
                {TIERS.map((t) => (
                  <td key={t.id} className={`px-5 py-4 text-center ${t.featured ? "bg-gold/10" : ""}`}>
                    <Link
                      to="/intake?type=consultation"
                      data-testid={`package-comparison-cta-${t.id}`}
                      className={`inline-block rounded-full px-5 py-2.5 text-xs font-display font-bold transition-colors ${
                        t.featured
                          ? "bg-gold text-obsidian hover:bg-gold-bright"
                          : "border border-gold/60 text-gold hover:bg-gold hover:text-obsidian"
                      }`}
                    >
                      Request a Quote
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Reveal>

      <Reveal delay={0.15} className="mt-10">
        <div className="rounded-2xl border border-gold/25 bg-obsidian-800/50 px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="packages-assessment-bridge">
          <p className="text-sm text-slate-300 text-center sm:text-left leading-relaxed">
            Not sure which package fits? Start with the{" "}
            <span className="text-gold font-semibold">$99.99 Business Growth Assessment</span> — your
            recommended package comes out of it.
          </p>
          <button
            data-testid="packages-bridge-cta"
            onClick={() => scrollToId("assessment")}
            className="shrink-0 rounded-full border border-gold/60 text-gold font-display font-bold px-6 py-3 text-sm hover:bg-gold hover:text-obsidian transition-colors"
          >
            Start With the Assessment
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);

export default ServicePackages;
